// CPU players. Pure and deterministic: given a state and a seat, a strategy
// picks one action from legalActions (or a discard/offer it builds itself), or
// returns null when that seat has nothing to do. `botMoves` batches what can
// happen together so callers can play a whole beat in one step.

import { basicAction } from './bot-basic';
import { smartAction } from './bot-smart';
import { applyAction } from './game';
import { COSTS, hasResources } from './resources';
import { canPlaceSettlement, discardOwed, legalRoadEdges, specialBuildOn } from './rules';
import { topologyOf } from './topology';
import type { Action, GameState } from './types';

export type BotKind = 'basic' | 'smart';
export const BOT_KINDS: readonly BotKind[] = ['basic', 'smart'];
export const DEFAULT_BOT: BotKind = 'smart';

export function isBotKind(x: unknown): x is BotKind {
  return typeof x === 'string' && (BOT_KINDS as readonly string[]).includes(x);
}

/** Which strategy plays a seat, or null for a human. */
export type BotOf = (seat: number) => BotKind | null;

export interface BotMove {
  seat: number;
  action: Action;
}

/** The next action for `player` under `kind`, or null if it's not their move. */
export function botAction(state: GameState, player: number, kind: BotKind = 'basic'): Action | null {
  const action = kind === 'smart' ? smartAction(state, player) : basicAction(state, player);
  if (action) return action;
  return wantsToBuildBetweenTurns(state, player) ? { type: 'requestBuild', on: true } : null;
}

/** Building between turns is on, and this CPU could afford something to build if it asked. */
function wantsToBuildBetweenTurns(state: GameState, player: number): boolean {
  if (!specialBuildOn(state) || player === state.currentPlayer || state.buildRequests?.[player]) return false;
  const kind = state.phase.kind;
  if (kind === 'setup' || kind === 'specialBuild' || kind === 'gameOver') return false;
  const p = state.players[player]!;
  const hand = p.resources;
  const t = topologyOf(state);
  if (hasResources(hand, COSTS.city) && p.citiesLeft > 0 && state.buildings.some((b) => b?.owner === player && b.kind === 'settlement')) return true;
  if (hasResources(hand, COSTS.settlement) && p.settlementsLeft > 0 && t.vertices.some((_, v) => canPlaceSettlement(state, player, v, false))) return true;
  if (hasResources(hand, COSTS.road) && p.roadsLeft > 0 && legalRoadEdges(state, player).length > 0) return true;
  return hasResources(hand, COSTS.devCard) && state.devDeck.length > 0;
}

function owesAnswer(state: GameState, seat: number): boolean {
  if (discardOwed(state, seat) > 0) return true;
  const trade = state.trade;
  return state.phase.kind === 'main' && !!trade && trade.from !== seat && trade.responses[seat] === null;
}

/**
 * Every CPU move that's due now, in the order to apply them:
 * - all owed discards and trade answers at once (they don't depend on each other);
 * - otherwise the current player's move, with a setup settlement followed straight away by its road.
 * Each move is legal in the state left by the ones before it.
 */
export function botMoves(state: GameState, botOf: BotOf): BotMove[] {
  const moves: BotMove[] = [];
  let s = state;
  const play = (seat: number, kind: BotKind): boolean => {
    const action = botAction(s, seat, kind);
    if (!action) return false;
    const r = applyAction(s, seat, action);
    if (!r.ok) return false;
    moves.push({ seat, action });
    s = r.state;
    return true;
  };

  for (let seat = 0; seat < s.players.length; seat++) {
    const kind = botOf(seat);
    if (kind && (owesAnswer(s, seat) || wantsToBuildBetweenTurns(s, seat))) play(seat, kind);
  }
  if (moves.length > 0) return moves;

  const kind = botOf(s.currentPlayer);
  if (!kind) return moves;
  const settling = s.phase.kind === 'setup' && s.phase.step === 'settlement';
  if (play(s.currentPlayer, kind) && settling) play(s.currentPlayer, kind);
  return moves;
}

/** The first move `botMoves` would make (kept for callers that step one action at a time). */
export function nextBotMove(state: GameState, botOf: BotOf): BotMove | null {
  return botMoves(state, botOf)[0] ?? null;
}

/**
 * A CPU's own trade offer is waiting on humans. Callers give them a while,
 * then call `abandonOffer` so a silent player can't stall the game.
 */
export function botAwaitingTrade(state: GameState, botOf: BotOf): boolean {
  const trade = state.trade;
  return state.phase.kind === 'main' && !!trade && trade.from === state.currentPlayer && botOf(trade.from) !== null && botMoves(state, botOf).length === 0;
}

export function abandonOffer(state: GameState): BotMove | null {
  return state.trade ? { seat: state.trade.from, action: { type: 'cancelTrade' } } : null;
}

/** How long people get to answer a trade offer before the CPUs at the table answer it. */
export const TRADE_GRACE_MS = 5000;

/** Someone who isn't a CPU still owes an answer to the open offer. */
export function humansStillAnswering(state: GameState, botOf: BotOf): boolean {
  const trade = state.trade;
  if (state.phase.kind !== 'main' || !trade) return false;
  return trade.responses.some((r, i) => i !== trade.from && r === null && botOf(i) === null);
}

/**
 * When to play a beat of CPU moves. Trade answers are instant when only CPUs
 * are answering, but wait out the grace period (counted from when the offer
 * was made) while people can still answer, so a person gets the first shot
 * at a trade. Everything else waits `normal` so people can follow it.
 */
export function botMovesAt(state: GameState, moves: readonly BotMove[], botOf: BotOf, now: number, normal: number, offeredAt: number): number {
  const answering = moves.length > 0 && moves.every((m) => m.action.type === 'respondTrade');
  if (!answering) return now + normal;
  return humansStillAnswering(state, botOf) ? Math.max(now + normal, offeredAt + TRADE_GRACE_MS) : now;
}
